/**
 * Azure Tool Executor - Executes Azure Logic Apps API calls for tools
 * Ported from copilot-agent-vscode frontend-new with MSAL token integration
 */

import type { ToolExecutionRequest, ToolExecutionResult } from '@/types/agui';
import type { ConnectionsConfiguration, ManagedApiConnection } from '@/types/logicApps';
import { fetchFuzzySearchOperations } from './operationsService';
import { BUILTIN_TEMPLATES } from '@/data/templates';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { validateWorkflow } from './draftWorkflowApi';

// Store the last discovered tool definitions for post-processing
let lastDiscoveredToolDefinitions: Record<string, unknown> | null = null;

/**
 * Get the last discovered tool definitions (for workflow post-processing)
 */
export function getLastDiscoveredToolDefinitions(): Record<string, unknown> | null {
  return lastDiscoveredToolDefinitions;
}

/**
 * Clear the last discovered tool definitions
 */
export function clearLastDiscoveredToolDefinitions(): void {
  lastDiscoveredToolDefinitions = null;
}

/**
 * Callback to be triggered when tool definitions are ready
 * This allows deferred patching of workflows that were applied before discovery completed
 */
let onToolDefinitionsReady: (() => void) | null = null;

export function setOnToolDefinitionsReadyCallback(callback: (() => void) | null): void {
  onToolDefinitionsReady = callback;
}

function notifyToolDefinitionsReady(): void {
  if (onToolDefinitionsReady) {
    console.log('[azureToolExecutor] Notifying tool definitions ready');
    onToolDefinitionsReady();
  }
}

/**
 * Patch Http actions in agent tools with discovered ApiConnection templates
 * Returns the patched workflow and a list of tools that were fixed
 */
export function patchHttpActionsInAgentTools(
  workflow: Record<string, unknown>
): { patched: Record<string, unknown>; fixedTools: string[] } {
  const fixedTools: string[] = [];

  console.log('[patchHttpActionsInAgentTools] Called at', Date.now(), 'toolDefinitions available:', !!lastDiscoveredToolDefinitions);
  if (!lastDiscoveredToolDefinitions) {
    console.log('[patchHttpActionsInAgentTools] No discovered tool definitions available');
    return { patched: workflow, fixedTools };
  }
  console.log('[patchHttpActionsInAgentTools] Available tools:', Object.keys(lastDiscoveredToolDefinitions));

  const definition = workflow.definition as Record<string, unknown> | undefined;
  if (!definition) {
    return { patched: workflow, fixedTools };
  }

  const actions = definition.actions as Record<string, Record<string, unknown>> | undefined;
  if (!actions) {
    return { patched: workflow, fixedTools };
  }

  // Deep clone to avoid mutating original
  const patchedWorkflow = JSON.parse(JSON.stringify(workflow)) as Record<string, unknown>;
  const patchedDefinition = patchedWorkflow.definition as Record<string, unknown>;
  const patchedActions = patchedDefinition.actions as Record<string, Record<string, unknown>>;

  // Find Agent actions and patch their tools
  for (const action of Object.values(patchedActions)) {
    if (action.type !== 'Agent') continue;

    const tools = action.tools as Record<string, Record<string, unknown>> | undefined;
    if (!tools) continue;

    // Check each tool
    for (const [toolName, tool] of Object.entries(tools)) {
      const toolActions = tool.actions as Record<string, Record<string, unknown>> | undefined;
      if (!toolActions) continue;

      // Check if any action is Http type
      let hasHttpAction = false;
      for (const toolAction of Object.values(toolActions)) {
        if (toolAction.type === 'Http') {
          hasHttpAction = true;
          break;
        }
      }

      if (!hasHttpAction) continue;

      // Try to find a matching discovered tool definition
      // Match by tool name or similar capability name
      const discoveredTool = findMatchingDiscoveredTool(toolName, lastDiscoveredToolDefinitions);

      if (discoveredTool) {
        const discoveredToolDef = discoveredTool.definition as Record<string, unknown>;
        const discoveredActions = discoveredToolDef.actions as Record<string, unknown> | undefined;

        if (discoveredActions) {
          // Check if the discovered tool has ApiConnection (not Http)
          const hasApiConnection = Object.values(discoveredActions).some(
            (a: unknown) => (a as Record<string, unknown>).type === 'ApiConnection'
          );

          if (hasApiConnection) {
            // Replace the tool's actions with the discovered ApiConnection actions
            console.log(`[patchHttpActionsInAgentTools] Patching tool "${toolName}" with discovered ApiConnection from "${discoveredTool.name}"`);

            // Update the actions
            tool.actions = discoveredActions;

            // Also update description and agentParameterSchema if available
            if (discoveredToolDef.description) {
              tool.description = discoveredToolDef.description;
            }
            if (discoveredToolDef.agentParameterSchema) {
              tool.agentParameterSchema = discoveredToolDef.agentParameterSchema;
            }

            fixedTools.push(toolName);
          }
        }
      }
    }
  }

  if (fixedTools.length > 0) {
    console.log(`[patchHttpActionsInAgentTools] Fixed ${fixedTools.length} tools:`, fixedTools);
  }

  return { patched: patchedWorkflow, fixedTools };
}

/**
 * Find a matching discovered tool by name similarity
 */
function findMatchingDiscoveredTool(
  toolName: string,
  toolDefinitions: Record<string, unknown>
): { name: string; definition: unknown } | null {
  const normalizedToolName = toolName.toLowerCase().replace(/[_-]/g, '');

  // First try exact match
  for (const [name, definition] of Object.entries(toolDefinitions)) {
    if (name.toLowerCase() === toolName.toLowerCase()) {
      return { name, definition };
    }
  }

  // Try normalized match
  for (const [name, definition] of Object.entries(toolDefinitions)) {
    const normalizedName = name.toLowerCase().replace(/[_-]/g, '');
    if (normalizedName === normalizedToolName) {
      return { name, definition };
    }
  }

  // Try partial match (tool name contains discovered name or vice versa)
  for (const [name, definition] of Object.entries(toolDefinitions)) {
    const normalizedName = name.toLowerCase().replace(/[_-]/g, '');
    if (normalizedToolName.includes(normalizedName) || normalizedName.includes(normalizedToolName)) {
      return { name, definition };
    }
  }

  // Try keyword matching
  const toolKeywords = toolName.toLowerCase().split(/[_-]/).filter(k => k.length > 2);
  for (const [name, definition] of Object.entries(toolDefinitions)) {
    const nameKeywords = name.toLowerCase().split(/[_-]/).filter(k => k.length > 2);
    const matchCount = toolKeywords.filter(k => nameKeywords.some(nk => nk.includes(k) || k.includes(nk))).length;
    if (matchCount >= 1 && matchCount >= toolKeywords.length / 2) {
      return { name, definition };
    }
  }

  console.log(`[findMatchingDiscoveredTool] No match found for tool "${toolName}"`);
  return null;
}

// Module-level cache for API responses to avoid duplicate API calls
// Key: full URL including query parameters, Value: { data: string, timestamp: number }
const apiResponseCache = new Map<string, { data: string; timestamp: number }>();
const API_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minute TTL

/**
 * Get cached response or null if not found/expired
 */
function getCachedResponse(url: string): string | null {
  const cached = apiResponseCache.get(url);
  if (cached && (Date.now() - cached.timestamp) < API_CACHE_TTL_MS) {
    console.log(`[AzureToolExecutor] Cache HIT for: ${url.substring(0, 100)}...`);
    return cached.data;
  }
  return null;
}

/**
 * Store response in cache
 */
function setCachedResponse(url: string, data: string): void {
  apiResponseCache.set(url, { data, timestamp: Date.now() });
  console.log(`[AzureToolExecutor] Cached response for: ${url.substring(0, 100)}...`);
}

export class AzureToolExecutor {
  private authToken: string;
  private projectName: string;
  private appName: string;
  private workflowName: string;

  constructor(
    authToken: string,
    projectName?: string,
    appName?: string,
    workflowName?: string
  ) {
    this.authToken = authToken;
    this.projectName = projectName || '';
    this.appName = appName || '';
    this.workflowName = workflowName || '';
  }

  /**
   * Execute a tool based on the request
   */
  async executeTool(request: ToolExecutionRequest): Promise<ToolExecutionResult> {
    try {
      let content: string;

      switch (request.tool_name) {
        case 'GetWorkflowSchema':
          content = await this.executeGetWorkflowSchema(request.arguments.section as string | undefined);
          break;
        case 'ValidateWorkflow':
          content = await this.executeValidateWorkflow(
            request.arguments.workflowDefinitionJson as string
          );
          break;
        case 'SearchOperations':
          // Search operations by text with optional connector name filter
          content = await this.executeSearchOperations(
            request.arguments.searchText as string,
            request.arguments.operationType as string | undefined,
            request.arguments.connectorNameFilter as string | undefined
          );
          break;
        case 'GetActionTemplate':
          // Step 3: Generate ready-to-use action template
          content = await this.executeGetActionTemplate(
            request.arguments.connectorName as string,
            request.arguments.operationId as string
          );
          break;
        case 'ListTemplates':
          content = this.executeListTemplates();
          break;
        default:
          throw new Error(`Unknown tool: ${request.tool_name}`);
      }

      return {
        execution_id: request.execution_id,
        content,
        is_error: false
      };
    } catch (error: unknown) {
      console.error(`[AzureToolExecutor] Error executing tool ${request.tool_name}:`, error);
      return {
        execution_id: request.execution_id,
        content: error instanceof Error ? error.message : 'Unknown error occurred',
        is_error: true
      };
    }
  }

  /**
   * ListTemplates - Returns metadata summaries for built-in workflow templates.
   * Community templates from Azure/LogicAppsTemplates are fetched at runtime by
   * the gallery UI; this tool only surfaces the curated built-in set so the LLM
   * always has a reliable baseline regardless of network availability.
   */
  private executeListTemplates(): string {
    const templates = BUILTIN_TEMPLATES.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      category: t.category,
      tags: t.tags,
      requiresConnections: t.connectionRequirements.length > 0,
      connectionCount: t.connectionRequirements.length,
      featured: t.featuredOrder !== undefined,
    }));

    const categories = [...new Set(templates.flatMap((t) => t.category))].sort();

    return JSON.stringify({ templates, total: templates.length, categories });
  }

  /**
   * GetWorkflowSchema - Returns Azure Logic Apps workflow definition schema
   * NOTE: This returns static schema information, no auth required
   */
  private async executeGetWorkflowSchema(_section?: string): Promise<string> {
    // Return static schema information (CORS prevents fetching from schema.management.azure.com)
    const schemaInfo = {
      schemaInfo: {
        schemaUrl: "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
        requiredProperties: ["$schema", "contentVersion"],
        structure: {
          description: "Top-level workflow definition structure",
          properties: {
            $schema: "Required. Must be the 2016-06-01 schema URL",
            contentVersion: "Required. Format: X.X.X.X (e.g., '1.0.0.0')",
            parameters: "Optional. Define workflow parameters including $connections",
            triggers: "Required for runnable workflows. Define trigger(s)",
            actions: "Optional. Define workflow actions",
            outputs: "Optional. Define workflow outputs"
          }
        }
      },
      triggers: {
        types: [
          {
            type: "Recurrence",
            description: "Schedule-based trigger for recurring workflows",
            template: {
              type: "Recurrence",
              recurrence: {
                frequency: "Day|Hour|Minute|Week|Month|Year",
                interval: 1,
                timeZone: "Pacific Standard Time",
                schedule: {
                  hours: ["9", "17"],
                  minutes: ["0", "30"],
                  weekDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
                }
              }
            }
          },
          {
            type: "Request",
            description: "HTTP request trigger (manual trigger)",
            template: {
              type: "Request",
              kind: "Http",
              inputs: {
                method: "POST",
                schema: {}
              }
            }
          },
          {
            type: "ApiConnection",
            description: "Connector-based polling trigger",
            template: {
              type: "ApiConnection",
              inputs: {
                host: {
                  connection: {
                    name: "${parameters('$connections')['connectorName']['connectionId']}"
                  }
                },
                method: "GET",
                path: "/trigger/path"
              },
              recurrence: {
                frequency: "Minute",
                interval: 5
              }
            }
          }
        ],
        validFrequencies: ["Second", "Minute", "Hour", "Day", "Week", "Month", "Year"]
      },
      actions: {
        types: [
          {
            type: "ApiConnection",
            description: "Call a managed connector operation",
            template: {
              type: "ApiConnection",
              inputs: {
                host: {
                  connection: {
                    name: "${parameters('$connections')['connectorName']['connectionId']}"
                  }
                },
                method: "POST",
                path: "/operation/path",
                body: {},
                queries: {}
              },
              runAfter: {}
            }
          },
          {
            type: "Compose",
            description: "Compose/transform data",
            template: {
              type: "Compose",
              inputs: "${variables('myVar')}",
              runAfter: {}
            }
          },
          {
            type: "InitializeVariable",
            description: "Initialize a variable",
            template: {
              type: "InitializeVariable",
              inputs: {
                variables: [
                  { name: "myVar", type: "String", value: "initial value" }
                ]
              },
              runAfter: {}
            }
          },
          {
            type: "Foreach",
            description: "Loop over an array, executing nested actions for each item. Use ${items('ForEachName')} to access the current item and ${iterationIndexes('ForEachName')} for the index.",
            template: {
              type: "Foreach",
              foreach: "${triggerBody()?.items}",
              actions: {
                Process_Item: {
                  type: "Http",
                  inputs: {
                    method: "POST",
                    uri: "https://api.example.com/process",
                    body: "${items('For_Each')}"
                  },
                  runAfter: {}
                }
              },
              runAfter: {}
            },
            notes: "First child action must have runAfter: {}. Use items('ForEachActionName') to reference the current item."
          }
        ]
      },
      expressions: {
        description: "Logic Apps expression syntax reference. Use ${...} wrapper for expressions.",
        basics: {
          parameterReference: "${parameters('paramName')}",
          variableReference: "${variables('varName')}",
          actionOutput: "${outputs('ActionName')}",
          actionBody: "${body('ActionName')}",
          triggerBody: "${triggerBody()}"
        },
        commonFunctions: {
          strings: ["concat(string1, string2, ...)", "substring(text, startIndex, length)", "replace(text, oldText, newText)"],
          collections: ["length(collection)", "first(collection)", "last(collection)", "contains(collection, value)"],
          loops: ["items('ForEachName') - current item in ForEach loop", "iterationIndexes('ForEachName') - current zero-based index in ForEach loop"],
          logical: ["if(condition, trueValue, falseValue)", "equals(value1, value2)", "and(expr1, expr2)", "or(expr1, expr2)"],
          datetime: ["utcNow()", "addDays(timestamp, days)", "formatDateTime(timestamp, format)"]
        }
      },
      templates: {
        minimalWorkflow: {
          description: "Minimal valid workflow definition",
          template: {
            $schema: "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
            contentVersion: "1.0.0.0",
            triggers: {},
            actions: {}
          }
        },
        scheduledWorkflowWithConnector: {
          description: "Scheduled workflow using a connector",
          template: {
            $schema: "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
            contentVersion: "1.0.0.0",
            parameters: {
              $connections: {
                type: "Object",
                defaultValue: {}
              }
            },
            triggers: {
              Recurrence: {
                type: "Recurrence",
                recurrence: {
                  frequency: "Day",
                  interval: 1,
                  schedule: {
                    hours: ["10"],
                    minutes: ["0"]
                  },
                  timeZone: "Pacific Standard Time"
                }
              }
            },
            actions: {
              Call_Connector: {
                type: "ApiConnection",
                inputs: {
                  host: {
                    connection: {
                      name: "${parameters('$connections')['connectorName']['connectionId']}"
                    }
                  },
                  method: "POST",
                  path: "/api/operation",
                  body: {}
                },
                runAfter: {}
              }
            }
          }
        }
      }
    };

    return JSON.stringify(schemaInfo, null, 2);
  }

  /**
   * GetConnectorSwagger - Retrieves Swagger/OpenAPI spec for a connector
   * Supports optional operationId filtering to return only specific operations
   * Uses unified URL-based caching to avoid duplicate API calls
   */
  private async executeGetConnectorSwagger(connectorName: string, operationId?: string): Promise<string> {
    if (!connectorName) {
      throw new Error('connectorName is required');
    }

    if (!this.projectName) {
      throw new Error('projectName is required for GetConnectorSwagger');
    }

    // Match MCP server URL with api-version=2022-09-01-preview
    const url = `${getLogicEndpoint()}/automationProjects/${this.projectName}/managedApis/${connectorName}/listSwagger?api-version=${apiVersions.otto}&export=true`;

    // Check cache first
    const cachedResponse = getCachedResponse(url);
    if (cachedResponse) {
      // If operationId is specified, filter the cached result
      if (operationId) {
        const cachedResult = JSON.parse(cachedResponse);
        return this.filterSwaggerByOperationId(cachedResult, operationId);
      }
      return cachedResponse;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    const resultJson = JSON.stringify(result, null, 2);

    // Cache the result
    setCachedResponse(url, resultJson);

    // If operationId is specified, filter the swagger to only that operation
    if (operationId) {
      return this.filterSwaggerByOperationId(result, operationId);
    }

    return resultJson;
  }

  /**
   * Filter swagger document to only include a specific operation
   */
  private filterSwaggerByOperationId(result: Record<string, unknown>, operationId: string): string {
    try {
      // Extract swagger document from the response
      let swaggerDoc: Record<string, unknown> = result;

      // Check if swagger is nested under properties
      const properties = result.properties as Record<string, unknown> | undefined;
      if (properties?.swagger) {
        swaggerDoc = properties.swagger as Record<string, unknown>;
      }

      // Check if this is already a swagger document
      if (!swaggerDoc.swagger && !swaggerDoc.openapi) {
        throw new Error('Swagger definition not found in response');
      }

      // Build filtered swagger with only the specified operation
      const filteredPaths: Record<string, Record<string, unknown>> = {};
      const filteredSwagger: Record<string, unknown> = {
        swagger: swaggerDoc.swagger,
        info: swaggerDoc.info,
        host: swaggerDoc.host,
        basePath: swaggerDoc.basePath,
        schemes: swaggerDoc.schemes,
        produces: swaggerDoc.produces,
        consumes: swaggerDoc.consumes,
        paths: filteredPaths
      };

      const referencedDefinitions = new Set<string>();

      // Find the operation with matching operationId
      const paths = swaggerDoc.paths as Record<string, Record<string, unknown>> | undefined;
      if (paths) {
        for (const [pathName, pathItem] of Object.entries(paths)) {
          for (const [method, operation] of Object.entries(pathItem)) {
            if (typeof operation === 'object' && operation !== null && (operation as Record<string, unknown>).operationId === operationId) {
              // Found the matching operation
              if (!filteredPaths[pathName]) {
                filteredPaths[pathName] = {};
              }
              filteredPaths[pathName][method] = operation;

              // Collect referenced definitions
              this.collectReferencedDefinitions(operation, referencedDefinitions);
            }
          }
        }
      }

      if (Object.keys(filteredPaths).length === 0) {
        throw new Error(`Operation '${operationId}' not found in swagger`);
      }

      // Include only referenced definitions
      const definitions = swaggerDoc.definitions as Record<string, unknown> | undefined;
      if (definitions && referencedDefinitions.size > 0) {
        const filteredDefinitions: Record<string, unknown> = {};
        for (const defName of referencedDefinitions) {
          if (definitions[defName]) {
            filteredDefinitions[defName] = definitions[defName];
          }
        }
        filteredSwagger.definitions = filteredDefinitions;
      }

      return JSON.stringify(filteredSwagger, null, 2);
    } catch (error: unknown) {
      return JSON.stringify({
        error: `Failed to filter swagger: ${error instanceof Error ? error.message : 'Unknown error'}`,
        fullSwagger: result
      }, null, 2);
    }
  }

  /**
   * Recursively collect all $ref references in the swagger document
   */
  private collectReferencedDefinitions(obj: unknown, definitions: Set<string>) {
    if (typeof obj !== 'object' || obj === null) return;

    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.collectReferencedDefinitions(item, definitions);
      }
    } else {
      for (const [key, value] of Object.entries(obj)) {
        if (key === '$ref' && typeof value === 'string' && value.startsWith('#/definitions/')) {
          definitions.add(value.substring('#/definitions/'.length));
        } else {
          this.collectReferencedDefinitions(value, definitions);
        }
      }
    }
  }

  /**
   * ValidateWorkflow - Validates workflow definition JSON
   * Uses Azure Logic Apps validate endpoint
   */
  private async executeValidateWorkflow(workflowDefinitionJson: string): Promise<string> {
    if (!workflowDefinitionJson) {
      throw new Error('workflowDefinitionJson is required');
    }

    if (!this.projectName) {
      throw new Error('projectName is required for ValidateWorkflow');
    }

    if (!this.appName) {
      throw new Error('appName is required for ValidateWorkflow');
    }

    if (!this.workflowName) {
      throw new Error('workflowName is required for ValidateWorkflow');
    }

    // Parse the workflow definition JSON
    let workflowDef: {
      definition: Record<string, unknown>;
      kind: string;
    };
    try {
      workflowDef = typeof workflowDefinitionJson === 'string'
        ? JSON.parse(workflowDefinitionJson)
        : workflowDefinitionJson;
    } catch (error: unknown) {
      return JSON.stringify({
        isValid: false,
        errors: [{
          type: 'JsonParse',
          message: error instanceof Error ? error.message : 'Unknown parse error'
        }]
      }, null, 2);
    }

    // Step 1: Local validation
    const localValidationErrors: Array<{ type: string; message: string; path?: string; severity?: string }> = [];

    // Check if definition property exists
    if (!workflowDef.definition) {
      localValidationErrors.push({
        type: 'Schema',
        message: 'Missing required "definition" property'
      });
    } else {
      const definition = workflowDef.definition as Record<string, unknown>;

      // Check required schema
      if (!definition.$schema) {
        localValidationErrors.push({
          type: 'Schema',
          path: '$schema',
          message: 'Missing required "$schema" property'
        });
      }

      // Check required contentVersion
      if (!definition.contentVersion) {
        localValidationErrors.push({
          type: 'Schema',
          path: 'contentVersion',
          message: 'Missing required "contentVersion" property'
        });
      }

      // Check triggers
      if (!definition.triggers || typeof definition.triggers !== 'object') {
        localValidationErrors.push({
          type: 'Schema',
          path: 'triggers',
          message: 'Workflow should have a "triggers" object'
        });
      }

      // Check for Http actions inside Agent tools - these should use ApiConnection
      const httpInToolsWarnings = this.detectHttpActionsInAgentTools(definition);
      if (httpInToolsWarnings.length > 0) {
        localValidationErrors.push(...httpInToolsWarnings);
      }
    }

    // Step 2: Azure API validation (or local runtime validation)
    const allErrors = [...localValidationErrors];

    // Build connectionConfig from referenceName values in the workflow definition
    // so the validation endpoint can resolve connection references.
    const connectionsConfig = this.extractDummyConnectionsConfig(workflowDef);

    try {
      const result = await validateWorkflow({
        projectName: this.projectName,
        appName: this.appName,
        workflowName: this.workflowName,
        workflowDefinition: workflowDef,
        connectionsConfig,
        getToken: () => Promise.resolve(this.authToken)
      });

      if (!result.isValid && !(result.error || '').includes('references an agent connection that doesn\'t exist')) {
        allErrors.push({
          type: 'AzureApi',
          message: result.error || 'Azure validation failed',
          severity: 'Error'
        });
      }
    } catch (error: unknown) {
      allErrors.push({
        type: 'AzureApi',
        message: `Could not perform Azure validation: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'Warning'
      });
    }

    return JSON.stringify({
      isValid: allErrors.length === 0,
      validationCount: allErrors.length,
      errors: allErrors
    }, null, 2);
  }

  /**
   * Extract connection references from a workflow definition and build a
   * ConnectionsConfiguration with stub entries so the validation endpoint
   * can resolve them.
   */
  private extractDummyConnectionsConfig(
    workflowDef: { definition?: unknown; kind?: string },
  ): ConnectionsConfiguration {
    const managedApiConnections: Record<string, ManagedApiConnection> = {};
    const definition = workflowDef.definition as Record<string, unknown> | undefined;
    if (!definition) return { managedApiConnections };

    const visit = (node: unknown): void => {
      if (!node || typeof node !== 'object') return;

      const obj = node as Record<string, unknown>;

      // Check for host.connection.referenceName + host.apiId
      const host = obj.host as Record<string, unknown> | undefined;
      if (host) {
        const conn = host.connection as Record<string, unknown> | undefined;
        const refName = conn?.referenceName as string | undefined;
        const apiId = (host.apiId as string) ?? '';

        if (refName && !managedApiConnections[refName]) {
          const connectorName = apiId.replace('/managedApis/', '') || 'unknown';
          managedApiConnections[refName] = {
            api: { id: apiId },
            connection: {
              id: `/automationProjects/${this.projectName}/applications/${this.appName}/connections/${refName}`,
            },
            connectionRuntimeUrl: `https://00000000000000000000000000000000.00.common.logic-df.azure-apihub.net/apim/${connectorName}/00000000000000000000000000000000`,
            authentication: { type: 'Raw' },
          };
        }
      }

      // Recurse into child objects/arrays
      for (const value of Object.values(obj)) {
        if (value && typeof value === 'object') {
          visit(value);
        }
      }
    };

    visit(definition.triggers);
    visit(definition.actions);

    return { managedApiConnections };
  }

  /**
   * Search operations by text with optional connector name filter.
   * Uses the runtime's fuzzy search API, then filters results by connector name if provided.
   */
  private async executeSearchOperations(
    searchText: string,
    operationType?: string,
    connectorNameFilter?: string
  ): Promise<string> {
    console.log('[SearchOperations] Called:', { searchText, operationType, connectorNameFilter });

    if (!this.projectName) {
      throw new Error('projectName is required for SearchOperations');
    }

    const actionType = operationType === 'trigger' ? 'trigger' as const
      : operationType === 'action' ? 'action' as const
      : undefined;

    const results = await fetchFuzzySearchOperations(
      { accessToken: this.authToken, projectName: this.projectName },
      searchText,
      actionType
    );

    console.log(`[SearchOperations] Got ${results.length} results for "${searchText}"`);

    // Map to a simpler structure
    let operations = results.map(op => {
      const apiId = op.properties?.api?.id ?? '';
      // Extract connector name from apiId (e.g., "/managedApis/icm" → "icm", "/serviceProviders/teams" → "teams")
      const connectorName = apiId.split('/').pop() ?? '';
      // Extract operationId from the resource name (e.g., "projects/.../apiOperations/SendEmailV2" → "SendEmailV2")
      const operationId = op.name?.split('/').pop() ?? '';
      return {
        connectorName,
        connectorDisplayName: op.properties?.api?.displayName ?? connectorName,
        operationId,
        operationDisplayName: op.properties?.summary ?? operationId,
        operationDescription: op.properties?.description ?? '',
        isTrigger: !!op.properties?.trigger,
        operationType: op.properties?.operationType ?? 'managed',
      };
    });

    // Apply connector name filter if provided
    if (connectorNameFilter) {
      const filter = connectorNameFilter.toLowerCase();
      const beforeCount = operations.length;
      operations = operations.filter(op => {
        const name = op.connectorName.toLowerCase();
        const displayName = op.connectorDisplayName.toLowerCase();
        return name.includes(filter) || displayName.includes(filter) || filter.includes(name);
      });
      console.log(`[SearchOperations] Filtered by connector "${connectorNameFilter}": ${beforeCount} → ${operations.length}`);
    }

    return JSON.stringify({
      searchText,
      connectorNameFilter: connectorNameFilter ?? null,
      resultCount: operations.length,
      operations: operations.slice(0, 20), // limit to top 20
    }, null, 2);
  }

  /**
   * Step 3: Generate ready-to-use action template for a connector + operation
   */
  private async executeGetActionTemplate(
    connectorName: string,
    operationId: string
  ): Promise<string> {
    console.log('[GetActionTemplate] Called for:', { connectorName, operationId });

    if (!this.projectName) {
      throw new Error('projectName is required');
    }

    // Fetch swagger and build action template
    let template: Record<string, unknown> | null = null;
    try {
      // Fetch full swagger (without operationId filter) to ensure we get complete paths
      const swaggerStr = await this.executeGetConnectorSwagger(connectorName);
      console.log(`[GetActionTemplate] Swagger response length for ${connectorName}: ${swaggerStr.length}`);
      template = this.buildActionTemplateFromSwagger(connectorName, swaggerStr, operationId);
      if (template) {
        // Verify it's not a default template (has real path)
        const inputs = template.inputs as Record<string, unknown> | undefined;
        if (inputs?.path === '/<see-connector-documentation>') {
          console.warn(`[GetActionTemplate] Got default template for ${connectorName}/${operationId} — swagger parsing likely failed`);
          template = null; // Force retry below
        }
      }
    } catch (error) {
      console.warn(`[GetActionTemplate] Swagger fetch failed for ${connectorName}:`, error);
    }

    // If swagger-based template failed, try with operationId filter
    if (!template) {
      try {
        const filteredSwagger = await this.executeGetConnectorSwagger(connectorName, operationId);
        console.log(`[GetActionTemplate] Filtered swagger for ${connectorName}/${operationId}: ${filteredSwagger.substring(0, 200)}`);
        template = this.buildActionTemplateFromSwagger(connectorName, filteredSwagger, operationId);
      } catch (error) {
        console.warn(`[GetActionTemplate] Filtered swagger also failed for ${connectorName}/${operationId}:`, error);
      }
    }

    if (!template) {
      template = this.getDefaultActionTemplate(connectorName, operationId);
    }

    const actionName = this.toActionName(operationId);

    const response = {
      toolDefinitions: {
        [actionName]: {
          description: `${operationId} action using the ${connectorName} connector.`,
          actions: {
            [actionName]: template
          }
        }
      },
      instructions: `Copy the action from toolDefinitions["${actionName}"].actions["${actionName}"] EXACTLY into your workflow. Add "runAfter": {} for sequencing. Do NOT modify host, apiId, operationId, method, or path. For agent workflows, include toolDefinitions in Agent_Loop.tools.`,
    };

    // Store for post-processing (agent workflows)
    lastDiscoveredToolDefinitions = { ...lastDiscoveredToolDefinitions, ...response.toolDefinitions };
    notifyToolDefinitionsReady();

    console.log('[GetActionTemplate] Response:', response);
    return JSON.stringify(response, null, 2);
  }

  /**
   * Convert capability name to action name (e.g., "query_kusto" -> "Query_Kusto")
   */
  private toActionName(capabilityName: string): string {
    return capabilityName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('_');
  }

  /**
   * Detect Http actions inside Agent tools that should use ApiConnection instead
   */
  private detectHttpActionsInAgentTools(
    definition: Record<string, unknown>
  ): Array<{ type: string; message: string; path?: string; severity?: string }> {
    const warnings: Array<{ type: string; message: string; path?: string; severity?: string }> = [];

    const actions = definition.actions as Record<string, Record<string, unknown>> | undefined;
    if (!actions) return warnings;

    // Find Agent actions
    for (const [actionName, action] of Object.entries(actions)) {
      if (action.type !== 'Agent') continue;

      const tools = action.tools as Record<string, Record<string, unknown>> | undefined;
      if (!tools) continue;

      // Check each tool for Http actions
      for (const [toolName, tool] of Object.entries(tools)) {
        const toolActions = tool.actions as Record<string, Record<string, unknown>> | undefined;
        if (!toolActions) continue;

        for (const [toolActionName, toolAction] of Object.entries(toolActions)) {
          if (toolAction.type === 'Http') {
            // This is an Http action inside a tool - should likely be ApiConnection
            warnings.push({
              type: 'HttpInAgentTool',
              path: `actions.${actionName}.tools.${toolName}.actions.${toolActionName}`,
              message: `Tool "${toolName}" uses Http action instead of ApiConnection. ` +
                `If a managed connector exists for this capability, use ApiConnection with the connector's referenceName. ` +
                `Re-call SearchConnectors to find the correct ApiConnection template for this tool.`,
              severity: 'Warning'
            });
          }
        }
      }
    }

    return warnings;
  }

  /**
   * Build an action template from swagger definition
   */
  private buildActionTemplateFromSwagger(
    connectorName: string,
    swaggerJson: string,
    operationId?: string
  ): Record<string, unknown> | null {
    try {
      const parsed = JSON.parse(swaggerJson);

      // Unwrap nested response: { properties: { swagger: { paths: ... } } }
      let swagger: Record<string, unknown> = parsed;
      const properties = parsed.properties as Record<string, unknown> | undefined;
      if (properties?.swagger) {
        swagger = properties.swagger as Record<string, unknown>;
      }

      // Find the operation path and method
      const paths = swagger.paths as Record<string, Record<string, unknown>> | undefined;
      if (!paths) {
        return this.getDefaultActionTemplate(connectorName);
      }

      // Find the first (or specified) operation
      for (const [pathName, pathItem] of Object.entries(paths)) {
        for (const [method, operation] of Object.entries(pathItem)) {
          if (method === 'parameters') continue; // Skip path-level parameters

          const op = operation as Record<string, unknown>;

          // If operationId specified, match it; otherwise take first operation
          if (operationId && op.operationId !== operationId) continue;

          // Extract parameters to build body schema
          const parameters = op.parameters as Array<Record<string, unknown>> | undefined;
          const bodyParam = parameters?.find(p => p.in === 'body');
          const queryParams = parameters?.filter(p => p.in === 'query') ?? [];

          // Build the action template
          const actionTemplate: Record<string, unknown> = {
            type: 'ApiConnection',
            inputs: {
              host: {
                apiId: '/managedApis/' + connectorName,
                operationId: op.operationId,
                connection: {
                  referenceName: connectorName
                }
              },
              method: method.toLowerCase(),
              path: pathName.replace('/{connectionId}', ''),
            }
          };

          // Add body if present
          if (bodyParam) {
            const bodySchema = bodyParam.schema as Record<string, unknown> | undefined;
            const bodyProperties = this.extractBodyProperties(bodySchema, swagger.definitions as Record<string, unknown> | undefined);
            if (Object.keys(bodyProperties).length > 0) {
              (actionTemplate.inputs as Record<string, unknown>).body = bodyProperties;
            }
          }

          // Add query parameters if present
          if (queryParams.length > 0) {
            const queries: Record<string, string> = {};
            for (const qp of queryParams) {
              queries[qp.name as string] = `<${qp.name}>`;
            }
            (actionTemplate.inputs as Record<string, unknown>).queries = queries;
          }

          console.log(`[buildActionTemplateFromSwagger] Built template for ${connectorName}/${operationId || 'first'}:`, actionTemplate);
          return actionTemplate;
        }
      }

      return this.getDefaultActionTemplate(connectorName, operationId);
    } catch (error) {
      console.warn(`[buildActionTemplateFromSwagger] Error parsing swagger for ${connectorName}:`, error);
      return this.getDefaultActionTemplate(connectorName, operationId);
    }
  }

  /**
   * Extract body properties from swagger schema
   */
  private extractBodyProperties(
    schema: Record<string, unknown> | undefined,
    definitions: Record<string, unknown> | undefined
  ): Record<string, unknown> {
    if (!schema) return {};

    // Handle $ref
    if (schema.$ref && typeof schema.$ref === 'string') {
      const refName = schema.$ref.replace('#/definitions/', '');
      const refDef = definitions?.[refName] as Record<string, unknown> | undefined;
      if (refDef) {
        return this.extractBodyProperties(refDef, definitions);
      }
    }

    // Handle object type with properties
    if (schema.properties) {
      const props = schema.properties as Record<string, Record<string, unknown>>;
      const result: Record<string, string> = {};
      for (const propName of Object.keys(props)) {
        // Use placeholder that indicates where to put agentParameters
        result[propName] = `\${agentParameters('${propName}')}`;
      }
      return result;
    }

    return {};
  }

  /**
   * Get a default action template when swagger parsing fails
   */
  private getDefaultActionTemplate(connectorName: string, operationId?: string): Record<string, unknown> {
    return {
      type: 'ApiConnection',
      inputs: {
        host: {
          apiId: `/managedApis/${connectorName}`,
          operationId: operationId ?? '<see-connector-documentation>',
          connection: {
            referenceName: connectorName
          }
        },
        method: 'post',
        path: '/<see-connector-documentation>',
        body: {}
      }
    };
  }

}
